import { IntegrationName } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import dayjs from 'dayjs';
import { useMutation, useQuery } from 'react-query';
import { DemoIntegrationOption, getDemoIntegrationLabel, ViewDemoSetupModal } from '@frontend/demo-location-ui';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { SchemaSalesDemoService } from '@frontend/schema';
import { useAppScopeCatalog, useAppScopeSetters } from '@frontend/scope';
import { ModalControlModalProps, useAlert } from '@frontend/design-system';
import { useLocationPickerDemoUiEnabled } from '../demo-ui-config';

const demoBundles = ['BASE', 'COORDINATE', 'ATTRACT'] as const;

type DemoBundle = (typeof demoBundles)[number];
type ViewDemoOrganization = {
  organizationName: string;
  name?: string;
  weaveBundle: DemoBundle[];
  integration: string;
  vertical?: string;
  includePhones: boolean;
  locationCount: 'single' | 'multiple';
  addons: string[];
  daysLeft?: number;
};

const isDemoBundle = (addon: string): addon is DemoBundle => {
  return demoBundles.includes(addon as DemoBundle);
};
const DEMO_INTEGRATION_OPTIONS: DemoIntegrationOption[] = [
  { value: 'none', label: 'None' },
  ...Object.values(IntegrationName)
    // remove unspecified integration from the list of options
    .slice(1)
    .map((integration) => ({
      value: integration,
      label: getDemoIntegrationLabel(integration),
    })),
];
export const ViewDemoSetupModalController = (props: ModalControlModalProps & { orgId: string }) => {
  const { t } = useTranslation('demo-location-ui');
  const { accessibleOrgData, accessibleScopeHierarchy } = useAppScopeCatalog();
  const showDemoUi = useLocationPickerDemoUiEnabled();
  const { setSelectedLocationIds, setSelectedOrgId, setSelectedScope, setSelectedGroupId } = useAppScopeSetters();
  const alerts = useAlert();

  const org = accessibleOrgData[props.orgId ?? ''];
  const orgLocationCount = Object.values(accessibleScopeHierarchy[props.orgId ?? ''] ?? {}).flat().length;

  const { data } = useQuery({
    queryFn: async () => {
      return SchemaSalesDemoService.GetSandbox(
        {
          sandboxId: org?.sandboxId ?? '',
        },
        { baseUrl: appConfig.BACKEND_API_V2 }
      );
    },
    queryKey: ['sandbox', org?.sandboxId],
    enabled: showDemoUi && !!org?.sandboxId && props.show,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return SchemaSalesDemoService.DeleteSandbox(
        {
          sandboxId: org?.sandboxId ?? '',
        },
        { baseUrl: appConfig.BACKEND_API_V2 }
      );
    },
    onMutate: () => {
      alerts.info(t('Deleting demo organization. This may take a minute or so. You will be redirected when complete.'));
      props.onClose();
    },
    onSuccess: () => {
      setSelectedLocationIds([]);
      setSelectedOrgId('');
      setSelectedScope(undefined);
      setSelectedGroupId('');
    },
  });

  if (!showDemoUi || !props.show || !data) {
    return null;
  }

  const { bundles, addons }: { bundles: DemoBundle[]; addons: string[] } = data.config?.addons
    ? data.config.addons.reduce(
        (acc: { bundles: DemoBundle[]; addons: string[] }, addon) => {
          if (isDemoBundle(addon)) {
            acc.bundles.push(addon);
          } else {
            acc.addons.push(addon);
          }
          return acc;
        },
        { bundles: [], addons: [] }
      )
    : { bundles: [], addons: [] };

  const organization: ViewDemoOrganization = {
    organizationName: org.name ?? '',
    name: org.name ?? '',
    weaveBundle: bundles,
    integration: data.config?.integrationName ?? 'DENTRIX_G5',
    vertical: data.config?.vertical ?? 'dental',
    includePhones: data.config?.phonesEnabled ?? true,
    locationCount: orgLocationCount > 1 ? 'multiple' : 'single',
    daysLeft: dayjs(data.expireTime).diff(dayjs(), 'day') ?? 20,
    addons,
  };

  return (
    <ViewDemoSetupModal
      {...props}
      organization={organization}
      integrationOptions={DEMO_INTEGRATION_OPTIONS}
      onRemove={() => {
        deleteMutation.mutate();
        props.onClose();
      }}
    />
  );
};
